import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  UserCheck,
  Clock,
  MessageSquare,
  Star,
  Wrench,
  MapPin,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { dashboardService, artisanService, contactMessageService } from "@/services/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { DashboardStats } from "@/types";

const statCards = [
  { key: "totalArtisans", label: "Artisans total", icon: Users, color: "bg-blue-500" },
  { key: "pendingArtisans", label: "En attente", icon: Clock, color: "bg-amber-500" },
  { key: "verifiedArtisans", label: "Vérifiés", icon: UserCheck, color: "bg-green-500" },
  { key: "totalMessages", label: "Messages", icon: MessageSquare, color: "bg-purple-500" },
  { key: "unreadMessages", label: "Non lus", icon: MessageSquare, color: "bg-red-500" },
  { key: "totalTestimonials", label: "Témoignages", icon: Star, color: "bg-pink-500" },
  { key: "totalServices", label: "Services", icon: Wrench, color: "bg-cyan-500" },
  { key: "totalCities", label: "Communes", icon: MapPin, color: "bg-orange-500" },
];

export function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dashboardService.getStats().then((data) => {
      setStats(data);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-dark">Dashboard</h1>
        <p className="text-gray-500 text-sm">Vue d'ensemble de votre plateforme</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, index) => {
          const value = stats?.[card.key as keyof DashboardStats] as number || 0;
          return (
            <motion.div
              key={card.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{card.label}</p>
                      <p className="text-3xl font-bold text-dark mt-1">{value}</p>
                    </div>
                    <div className={`w-10 h-10 ${card.color} rounded-lg flex items-center justify-center`}>
                      <card.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Messages récents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.recentMessages.slice(0, 5).map((msg) => (
                <div key={msg.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100" onClick={() => navigate('/admin/messages')}>
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-dark truncate">{msg.name}</p>
                    <p className="text-xs text-gray-500 truncate">{msg.subject}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    msg.status === "new" ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"
                  }`}>
                    {msg.status}
                  </span>
                </div>
              ))}
              {(!stats?.recentMessages.length) && (
                <p className="text-gray-400 text-sm text-center py-4">Aucun message récent</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Artisans récents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.recentArtisans.slice(0, 5).map((artisan) => (
                <div key={artisan.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-dark truncate">
                      {artisan.firstName} {artisan.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{artisan.service} / {artisan.city}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    artisan.status === "verified" ? "bg-green-100 text-green-600" :
                    artisan.status === "pending" ? "bg-amber-100 text-amber-600" :
                    "bg-red-100 text-red-600"
                  }`}>
                    {artisan.status}
                  </span>
                </div>
              ))}
              {(!stats?.recentArtisans.length) && (
                <p className="text-gray-400 text-sm text-center py-4">Aucun artisan récent</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
